import { InfraScene } from "../scenes/internal";
import { isDefined } from "../../internal";
import { QuadtreeUtils } from "../../utils/internal";
import { SceneGraphNode } from "../internal";
import { DisparityTile } from "./internal";
/**
 * Quadtree Disparity  Class
 * Map Coordinates are:
 *
 *   <img src="../../art/quadtreedefinition.png" alt="" />
 *
 * @author Robert Wüest, Benjamin Loesch, Martin Christen
 * @class DisparityQuadtree
 * @param {SceneGraphNode} parentNode
 * @extends {SceneGraphNode}
 * @constructor
 */
export class DisparityQuadtree extends SceneGraphNode {
    /**
     * @method constructor
     * @param {string[]} baseUrls
     * @param {number} tileSize tile size in pixel e.g. 512
     * @param {number} dm_imgwidth full (untiled) depthmap width in maxZoom e.g. 2004
     * @param {number} dm_imgheight full (untiled) depthmap height in maxZoom lod e.g. 1336
     * @param {number} minZoom
     * @param {number} maxZoom
     */
    constructor(baseUrls, tileSize, dm_imgwidth, dm_imgheight, minZoom, maxZoom) {
        super(null);
        // In future, this data must be in a "dataset description file".
        /**
         * @property {number} tileSize
         * @default 512
         */
        this.tileSize = 512;
        /**
         * @property {number} maxZoom
         * @default 3
         */
        this.maxZoom = 3;
        /**
         * @property {number} minZoom
         * @default 1
         */
        this.minZoom = 1;
        /**
         * image width in zoom level 1;
         * @property {number} level1Width
         * @default 0
         */
        this.level1Width = 0;
        /**
         * image height in zoom level 1;
         * @property {number} level1Height
         * @default 0
         */
        this.level1Height = 0;
        /**
         * Holds all visible tiles
         * @property {DisparityTile[]} lstFrustumTiles
         * @default []
         */
        this.lstFrustumTiles = [];
        /**
         * list all available tiles
         * @property {DisparityTile[]} tiles
         * @default {}
         */
        this.tiles = {};
        /**
         * Holds all registered tileready callback functions
         * @type {{}}
         */
        this.tilesreadycbf = {};
        /**
         * Holds all registered tileemptyready callback functions
         * @type {{}}
         */
        this.tilesemptyreadycbf = {};
        /**
         * @property {number} panX
         * @default 0
         */
        this.panX = 0;
        /**
         * @property {number} panY
         * @default 0
         */
        this.panY = 0;
        /**
         * @property {number} zoom
         * @default 1.0
         */
        this.zoom = 1.0;
        /**
         * @property {number} curZoom
         * @default 1
         */
        this.curZoom = 1;
        /**
         * @property {string[]} baseUrls
         * @default []
         */
        this.baseUrls = [];
        /**
         * @property {number} _currentImageIndex
         * @default 0
         * @private
         */
        this.currentImageIndex = 0;
        /**
         * @property {number} _cachingRange
         * @default 0
         * @private
         */
        this._cachingRange = 0;
        /**
         * @property {number} _cachingDepth
         * @default 0
         * @private
         */
        this._cachingDepth = 0;
        /**
         * is true when the caching cicle is completed
         * @property {boolean} _cachingCompleted
         * @default true
         * @private
         */
        this._cachingCompleted = true;
        /**
         * data width in normalized quadtree coordinates
         * @property {number} imageWidth
         * @default 0
         */
        this.dataWidth = 0;
        /**
         * data height in normalized quadtree coordinates
         * @property {number} dataHeight
         * @default 0
         */
        this.dataHeight = 0;
        /**
         * depthmap width -> full width e.g. 2484;
         * @property {number} imageWidth
         * @default 0
         */
        this.dm_imgwidth = 0;
        /**
         * depthmap width -> full height e.g. 2048;
         * @property {number} dataHeight
         * @default 0
         */
        this.dm_imgheight = 0;
        /**
         * Semaphore to trigger render cycles (-1: 2 more Render cycles will be triggered  0: default 1: rendering done)
         * @property {number} _renderCycle
         * @default 0
         * @private
         */
        this._renderCycle = 0;
        /**
         * @property {Object} _iterator
         * @default { count: 0 }
         * @private
         */
        this._iterator = { count: 0 };
        this.baseUrls = baseUrls;
        this.tileSize = tileSize;
        // calculate image width / height in normalized quadtree coordinates
        const quadtreesize = Math.pow(2, maxZoom) * tileSize;
        this.dm_imgwidth = dm_imgwidth;
        this.dm_imgheight = dm_imgheight;
        this.dataWidth = dm_imgwidth / quadtreesize;
        this.dataHeight = dm_imgheight / quadtreesize;
        this.minZoom = minZoom;
        this.maxZoom = maxZoom;
    }
    /**
     * @method init
     * @param {TraversalState} traversalState
     */
    init(traversalState) {
        super.init(traversalState);
    }
    /**
     * @method exit
     */
    exit() {
        return;
    }
    /**
     * @method destroy
     */
    destroy() {
        this.wipeTiles();
        this.currentTraversalState.scene.engine.imageCache.clear();
    }
    /**
     * @method render
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        super.render(traversalState);
        const engine = traversalState.scene.engine;
        if (!(traversalState.scene instanceof InfraScene)) {
            throw "Scene Error: infra3d.DisparityQuadtree can only be attachted to type InfraScene";
        }
        // Travers
        this.zoom = traversalState.zoom;
        this.panX = traversalState.panX;
        this.panY = traversalState.panY;
        //Originally the floor value of zoom was used here, but this leads to
        //oversampling (due to the zoom, the screenres was higher than the
        this.curZoom = Math.min(Math.max(Math.ceil(this.zoom), 1), this.maxZoom);
        this.level1Width = engine.getContextWidth();
        while (this.level1Width > 2 * this.tileSize) {
            this.level1Width /= 2;
        }
        this.level1Height = engine.getContextHeight();
        while (this.level1Height > 2 * this.tileSize) {
            this.level1Height /= 2;
        }
        // Render
        if (this.baseUrls[this.currentImageIndex] != "") {
            // Calculate optimized frustum list:
            this._renderCycle += 1;
            // Calculate optimized frustum list:
            this._calcFrustumList();
            if (this._renderCycle < 1) {
                traversalState.lockRendering = true;
            }
            else {
                this._renderCycle = 0;
            }
        }
    }
    /**
     * @method mouseToMap
     * @param mx
     * @param my
     * @return {*[]}
     */
    mouseToMap(mx, my) {
        return [(mx - this.panX) / this.zoom / (2 * this.tileSize), (my - this.panY) / this.zoom / (2 * this.tileSize)];
    }
    /**
     * convert map position (pixel) to mouse position
     * @method mapToMouse
     * @param {number} px
     * @param {number} py
     * @return {*[]}
     */
    mapToMouse(px, py) {
        return [px * 2 * this.tileSize * this.zoom + this.panX, py * 2 * this.tileSize * this.zoom + this.panY];
    }
    /**
     * @method _subDivide
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @private
     */
    _subDivide(x0, y0, x1, y1) {
        const i = this._iterator.count;
        if (this.lstFrustumTiles[i].quadCode.length == this.curZoom) {
            this._iterator.count++;
            return; // no further subdivision: only zoom to current zoom level!
        }
        const coords = QuadtreeUtils.QuadKeyToNormalizedCoord(this.lstFrustumTiles[i].quadCode);
        // check if this.lstFrustum[i]  is inside (x0,y0)-(x1,y1)
        if (!(coords[0] > x1 || coords[2] < x0 || coords[1] > y1 || coords[3] < y0)) {
            const quadCodeTop = this.lstFrustumTiles[i].quadCode;
            for (let j = 0; j < 4; j++) {
                if (!isDefined(this.tiles[quadCodeTop + j.toString()]) ||
                    this.tiles[quadCodeTop + j.toString()] == null) {
                    this.tiles[quadCodeTop + j.toString()] = new DisparityTile(quadCodeTop + j.toString(), this.baseUrls[this.currentImageIndex], this.dataWidth, this.dataHeight, this.tileSize, this.currentTraversalState);
                }
            }
            if (this.tiles[quadCodeTop + "0"].isAvailable() &&
                this.tiles[quadCodeTop + "1"].isAvailable() &&
                this.tiles[quadCodeTop + "2"].isAvailable() &&
                this.tiles[quadCodeTop + "3"].isAvailable()) {
                this.lstFrustumTiles.splice(i, 1);
                for (let k = 0; k < 4; k++) {
                    this.lstFrustumTiles.push(this.tiles[quadCodeTop + k.toString()]);
                }
            }
            else {
                this._renderCycle = -1;
                this._iterator.count++;
            }
            return;
        }
        this._iterator.count++;
    }
    /**
     * Calc quadtree (private function, do not call)
     * @method _divide
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @private
     */
    _divide(x0, y0, x1, y1) {
        this._iterator.count = 0;
        while (this._iterator.count != this.lstFrustumTiles.length) {
            this._subDivide(x0, y0, x1, y1);
        }
    }
    /**
     * Optimize frustum list (private function, do not call)
     * @method _Optimize
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @private
     */
    _optimize(x0, y0, x1, y1) {
        let i = this.lstFrustumTiles.length - 1;
        while (i > -1) {
            const tile = this.lstFrustumTiles[i];
            if (tile.noData) {
                this.lstFrustumTiles.splice(i, 1);
            }
            else {
                if (tile.extent[0] > x1 || tile.extent[2] < x0 || tile.extent[1] > y1 || tile.extent[3] < y0) {
                    this.lstFrustumTiles.splice(i, 1);
                }
            }
            i--;
        }
    }
    /**
     * Calculate quadtree (private function, do not call)
     * @method _calcFrustumList
     * @private
     * @return {boolean} True if Frustum is ready to be rendered, else returns false.
     */
    _calcFrustumList() {
        let tileReady;
        const tileEmptyReady = (tile) => {
            console.log("empty tile");
        };
        for (let j = 0; j < 4; j++) {
            // create tiles if not visible
            if (!isDefined(this.tiles[j.toString()]) || this.tiles[j.toString()] == null) {
                this.tiles[j.toString()] = new DisparityTile(j.toString(), this.baseUrls[this.currentImageIndex], this.dataWidth, this.dataHeight, this.tileSize, this.currentTraversalState, tileReady, tileEmptyReady);
            }
        }
        const ctx = this.currentTraversalState.scene.engine.renderer.domElement;
        const r00 = this.mouseToMap(0, 0);
        const r11 = this.mouseToMap(ctx.width - 1, ctx.height - 1);
        // cleanup frustum first
        this.lstFrustumTiles = [];
        // add the first 4 tiles LOD 1
        let lod1Unavailable = false;
        for (let k = 0; k < 4; k++) {
            if (!this.tiles[k.toString()].isAvailable()) {
                this._renderCycle = -1;
                lod1Unavailable = true;
            }
            this.lstFrustumTiles.push(this.tiles[k.toString()]);
        }
        if (!lod1Unavailable) {
            this._divide(r00[0], r00[1], r11[0], r11[1]); // Subdivide
            this._optimize(r00[0], r00[1], r11[0], r11[1]); // Optimize frustum list
        }
        /*
            QUADTREE DEBUG FUNCTION
            */
        const showQuadTree = function (frustumList) {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = 128;
            canvas.height = 128;
            for (let rx = 0; rx < frustumList.length; rx++) {
                const tileCoords = QuadtreeUtils.QuadKeyToTileCoord(frustumList[rx].quadCode);
                const tileSize = Math.floor(128.0 / Math.pow(2, frustumList[rx].quadCode.length));
                ctx.beginPath();
                ctx.rect(tileCoords.x * tileSize, tileCoords.y * tileSize, tileSize, tileSize);
                ctx.lineWidth = 2;
                ctx.strokeStyle =
                    frustumList[rx].quadCode.length > 2
                        ? "#DDAA00"
                        : frustumList[rx].quadCode.length > 1
                            ? "#DD6600"
                            : "#DD0000";
                ctx.stroke();
            }
            // create a new img object
            const image = new Image();
            // set the img.src to the canvas data url
            image.src = canvas.toDataURL();
        };
        return true;
    }
    /**
     * Wipes all tiles from catalog, frustum and scene. this is used before loading a new tiles image
     * @method wipeTiles
     */
    wipeTiles() {
        for (let i = 0; i < this.lstFrustumTiles.length; i++) {
            // clear frustum
            this.lstFrustumTiles = [];
            // clear tile catalog
            for (const qc in this.tiles) {
                if (this.tiles[qc] != null)
                    this.tiles[qc].destroy();
                this.tiles[qc] = null;
                this.tilesreadycbf[qc] = null;
                this.tilesemptyreadycbf[qc] = null;
            }
        }
        this.currentTraversalState.scene.engine.dispMapCache.clear();
    }
    /**
     * Read and return the disparity value
     * @param {number} x
     * @param {number} y
     * @param {boolean} absolute
     * @return {number[]} array containing [disparity ,mapx, mapy, quality]
     */
    readDisparity(x, y, absolute) {
        let map = [0, 0];
        if (isDefined(absolute) && absolute == true) {
            map = [x, y];
        }
        else {
            map = this.mouseToMap(x, y);
        }
        for (let i = 0; i < this.lstFrustumTiles.length; i++) {
            const tile = this.lstFrustumTiles[i];
            if (map[0] >= tile.extent[0] &&
                map[0] < tile.extent[2] &&
                map[1] >= tile.extent[1] &&
                map[1] < tile.extent[3]) {
                // point is inside this tile!
                const retVal = tile.getDisparityValue(map[0], map[1]);
                return [retVal[0], map[0], map[1], retVal[1]];
            }
        }
        return [0, map[0], map[1], 0]; // nothing found, return no data value
    }
    /**
     * Wipe all active tiles and set the current quadtree image set
     * @method setImageIndex
     * @param {number} index
     *
     */
    setDataIndex(index) {
        if (this.currentImageIndex == index)
            return;
        this.wipeTiles();
        this.currentImageIndex = index;
    }
    /**
     * Request a disparity tile and register tileready and tileemptyready callback.
     * Writes the disparity tile to the disptiles-array.
     * Multiple callbacks can be registered and emited.
     * @param quadcode
     * @param sensorid
     * @param tileready
     * @param tileemptyready
     */
    getDisparityTile(quadcode, sensorid = 0, tileready, tileemptyready) {
        if (!isDefined(this.tiles[quadcode]) || this.tiles[quadcode] == null) {
            //create a new disparity tile if not existant
            const tileReady = (tile) => {
                //emit registered tileready-callback functions
                for (let i = 0; i < this.tilesreadycbf[tile.quadCode].length; i++) {
                    this.tilesreadycbf[tile.quadCode][i](tile);
                }
            };
            const tileEmptyReady = (tile) => {
                //emit registered tileemptyready-callback functions
                for (let i = 0; i < this.tilesemptyreadycbf[tile.quadCode].length; i++) {
                    this.tilesemptyreadycbf[tile.quadCode][i](tile);
                }
            };
            this.tilesreadycbf[quadcode] = [];
            this.tilesemptyreadycbf[quadcode] = [];
            this.tiles[quadcode] = new DisparityTile(quadcode, this.baseUrls[this.currentImageIndex], this.dataWidth, this.dataHeight, this.tileSize, this.currentTraversalState, tileReady, tileEmptyReady);
        }
        if (this.tiles[quadcode].isAvailable()) {
            //emit tileready if the disptile is already loaded (e.g. the dispmap had been loaded from cache)
            if (tileready)
                tileready(this.tiles[quadcode]);
        }
        else {
            //register tileready and tileemptyready-callback functions if the tile is not yet loaded.
            if (tileready)
                this.tilesreadycbf[quadcode].push(tileready);
            if (tileemptyready)
                this.tilesemptyreadycbf[quadcode].push(tileemptyready);
        }
        return this.tiles[quadcode];
    }
}
//# sourceMappingURL=disparityquadtree.js.map